import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Package, Ruler, Weight, ShoppingCart, Heart, Share, Download, Plus, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { productsApi, type ProductDetail, SearchItem } from "@/services/productsApi";
import { useCart } from "@/contexts/CartContext";

export default function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [productImage, setProductImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [similarProducts, setSimilarProducts] = useState<SearchItem[]>([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);

  useEffect(() => {
    if (!slug) {
      setError("Slug du produit manquant");
      setLoading(false);
      return;
    }

    loadProduct();
  }, [slug]);

  const loadProduct = async () => {
    if (!slug) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await productsApi.getProductBySlug(slug);
      setProduct(data);
      
      // Fetch product image separately
      try {
        const imageData = await productsApi.getProductImage(data.id);
        if (imageData.image_base64) {
          setProductImage(`data:image/jpeg;base64,${imageData.image_base64}`);
        }
      } catch (imageError) {
        console.error("Error fetching product image:", imageError);
      }
      // Load similar products after getting product info
      if (data.id) {
        loadSimilarProducts();
      }
    } catch (error) {
      console.error("Failed to load product:", error);
      setError("Produit non trouvé");
    } finally {
      setLoading(false);
    }
  };

  const loadSimilarProducts = async () => {
    setLoadingSimilar(true);
    try {
      // Get random products from the same category or general search
      const response = await productsApi.searchProducts({
        page_number: 1,
        page_size: 4,
      });
      setSimilarProducts(response.items);
    } catch (error) {
      console.error("Failed to load similar products:", error);
    } finally {
      setLoadingSimilar(false);
    }
  };

  const handleQuantityChange = (increment: boolean) => {
    if (increment) {
      setQuantity(prev => prev + 1);
    } else if (quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;

    addToCart({
      id: product.id.toString(),
      name: product.name,
      price: 0, // No price in new API
      image: productImage || undefined,
      sku: product.id.toString(), // Use ID as SKU fallback
    }, quantity);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: product?.name,
        url: window.location.href,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const goBack = () => {
    // Try to preserve previous search filters
    const lastSearch = sessionStorage.getItem("lastProductSearch");
    if (lastSearch) {
      navigate(`/produits?${lastSearch}`);
    } else {
      navigate("/produits");
    }
  };

  const handleDownloadTechnicalSheet = async () => {
    if (!product) return;
    try {
      const blob = await productsApi.downloadTechnicalSheet(product.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fiche-technique-${product.slug || product.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading technical sheet:', error);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="mb-6">
          <Skeleton className="h-10 w-48" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Skeleton className="aspect-square w-full" />
          <div className="space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold mb-4">Produit non trouvé</h1>
          <p className="text-muted-foreground mb-6">{error || "Le produit demandé n'existe pas."}</p>
          <Button onClick={goBack} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour à la liste
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      {/* Breadcrumb */}
      <div className="mb-6">
        <Button onClick={goBack} variant="ghost" className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour à la liste
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Product Image - Left Side */}
        <div className="aspect-square bg-muted rounded-lg overflow-hidden">
          {productImage ? (
            <img 
              src={productImage} 
              alt={product.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <Package className="h-16 w-16" />
              <span className="ml-2">Image non disponible</span>
            </div>
          )}
        </div>

        {/* Product Information Card - Right Side */}
        <div className="space-y-6">
          <Card>
            <CardContent className="p-6 space-y-4">
              {/* Brand */}
              {product.brand && (
                <div className="text-sm text-muted-foreground uppercase tracking-wide">
                  {product.brand}
                </div>
              )}
              
              {/* Name */}
              <h1 className="text-2xl font-bold">{product.name}</h1>
              
              {/* Short Description */}
              {product.ecommerce_short_description && (
                <p className="text-muted-foreground">{product.ecommerce_short_description}</p>
              )}
              
              {/* SKU */}
              <div className="text-sm">
                <span className="font-medium">SKU: </span>
                <span className="text-muted-foreground">{product.id}</span>
              </div>
              
              {/* Note: Price not available in current API */}
              
              {/* Quantity and Add to Cart */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium">Quantité:</label>
                  <div className="flex items-center border rounded-md">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleQuantityChange(false)}
                      className="h-8 w-8 p-0"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <Input 
                      type="number" 
                      value={quantity} 
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="h-8 w-16 text-center border-0 focus-visible:ring-0"
                      min="1"
                    />
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleQuantityChange(true)}
                      className="h-8 w-8 p-0"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <Button onClick={handleAddToCart} className="w-full" size="lg">
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Ajouter au panier
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleShare} className="flex-1">
              <Share className="h-4 w-4 mr-2" />
              Partager
            </Button>
            <Button variant="outline" className="flex-1">
              <Heart className="h-4 w-4 mr-2" />
              Favoris
            </Button>
            <Button variant="outline" className="flex-1" onClick={handleDownloadTechnicalSheet}>
              <Download className="h-4 w-4 mr-2" />
              Fiche technique
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <Tabs defaultValue="description" className="mb-8">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="description">Description</TabsTrigger>
          <TabsTrigger value="dimensions">Dimensions</TabsTrigger>
          <TabsTrigger value="characteristics">Caractéristiques</TabsTrigger>
        </TabsList>
        
        <TabsContent value="description" className="mt-6">
          {product.ecommerce_sale_description ? (
            <Card>
              <CardContent className="p-6">
                <div 
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: product.ecommerce_sale_description }}
                />
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6">
                <p className="text-muted-foreground">Aucune description disponible.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="dimensions" className="mt-6">
          <Card>
            <CardContent className="p-6 space-y-4">
              {/* Dimensions */}
              {(product.length || product.width || product.height) ? (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Ruler className="h-4 w-4" />
                    Dimensions
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {product.length && (
                      <div className="text-center p-3 bg-muted rounded">
                        <div className="text-lg font-medium">{product.length} mm</div>
                        <div className="text-sm text-muted-foreground">Longueur</div>
                      </div>
                    )}
                    {product.width && (
                      <div className="text-center p-3 bg-muted rounded">
                        <div className="text-lg font-medium">{product.width} mm</div>
                        <div className="text-sm text-muted-foreground">Largeur</div>
                      </div>
                    )}
                    {product.height && (
                      <div className="text-center p-3 bg-muted rounded">
                        <div className="text-lg font-medium">{product.height} mm</div>
                        <div className="text-sm text-muted-foreground">Hauteur</div>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
              
              {/* Weight and Volume */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              </div>
              
              {!product.length && !product.width && !product.height && (
                <p className="text-muted-foreground">Aucune information de dimensions disponible.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="characteristics" className="mt-6">
          <Card>
            <CardContent className="p-6">
              {product.attributes.length > 0 ? (
                <div className="space-y-6">
                  {product.attributes.map((attribute, index) => (
                    <div key={attribute.attribute_name}>
                      <h4 className="font-medium mb-3">{attribute.attribute_name}</h4>
                      <div className="flex flex-wrap gap-2">
                        {attribute.values.map((value) => (
                          <Badge key={value} variant="outline">
                            {value}
                          </Badge>
                        ))}
                      </div>
                      {index < product.attributes.length - 1 && <Separator className="mt-4" />}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">Aucune caractéristique disponible.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Similar Products */}
      <section>
        <h2 className="text-2xl font-bold mb-6">Produits similaires</h2>
        {loadingSimilar ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <Skeleton className="w-full h-48 mb-4" />
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2 mb-2" />
                  <Skeleton className="h-6 w-1/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : similarProducts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {similarProducts.map((similarProduct) => (
              <Card key={similarProduct.id} className="group hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  {/* Product Image */}
                  <div className="aspect-square mb-4 bg-muted rounded overflow-hidden relative">
                    {similarProduct.image_base64 ? (
                      <img
                        src={`data:image/jpeg;base64,${similarProduct.image_base64}`}
                        alt={similarProduct.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        <Package className="h-8 w-8" />
                      </div>
                    )}
                  </div>

                  {/* Product Info - Centered */}
                  <div className="text-center space-y-2">
                    {/* Title */}
                    <h3 className="font-semibold text-sm line-clamp-2">{similarProduct.name}</h3>
                    
                    {/* SKU */}
                    <p className="text-xs text-muted-foreground">ID: {similarProduct.id}</p>
                    
                    {/* Note: Price not available in current API */}
                  </div>
                  
                  {/* View Product Button */}
                  <div className="mt-3">
                    {similarProduct.slug ? (
                      <Link to={`/produit/${similarProduct.slug}`} className="w-full">
                        <Button variant="outline" size="sm" className="w-full">
                          Voir le produit
                        </Button>
                      </Link>
                    ) : (
                      <Button variant="outline" size="sm" className="w-full" disabled>
                        Produit indisponible
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground">Aucun produit similaire trouvé.</p>
            </CardContent>
          </Card>
        )}
      </section>
    </div>
  );
}